import React, { useState } from 'react';
import { ScheduleItem, TimeOfDay, LocationType } from '../types';
import { Plus, Trash2, Save, X, Calendar } from 'lucide-react';

interface ScheduleEditorProps {
  schedule: ScheduleItem[];
  onChange: (schedule: ScheduleItem[]) => void;
}

const ScheduleEditor: React.FC<ScheduleEditorProps> = ({ schedule, onChange }) => {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<ScheduleItem | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  // Empty form template
  const emptyForm: ScheduleItem = {
    date: '',
    timeOfDay: TimeOfDay.AllDay,
    location: '',
    locationType: LocationType.Clinic,
    address: '',
    activity: '',
    notes: ''
  };

  const handleAdd = () => {
    setIsAdding(true);
    setEditForm({ ...emptyForm });
  };

  const handleEdit = (index: number) => {
    setEditingIndex(index);
    setEditForm({ ...schedule[index] });
  };

  const handleSave = () => {
    if (!editForm) return;

    // Validation
    if (!editForm.date || !editForm.location || !editForm.activity) {
      alert('請填寫必要欄位：日期、地點、活動類型');
      return;
    }

    let updatedSchedule: ScheduleItem[];

    if (isAdding) {
      updatedSchedule = [...schedule, editForm];
    } else if (editingIndex !== null) {
      updatedSchedule = [...schedule];
      updatedSchedule[editingIndex] = editForm;
    } else {
      return;
    }

    onChange(updatedSchedule);
    handleCancel();
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingIndex(null);
    setEditForm(null);
  };

  const handleDelete = (index: number) => {
    if (window.confirm('確定要刪除這個行程項目嗎？')) {
      const updatedSchedule = schedule.filter((_, i) => i !== index);
      onChange(updatedSchedule);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Calendar className="text-indigo-600" size={20} />
          <h3 className="text-lg font-bold text-gray-800">詳細行程安排</h3>
          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
            共 {schedule.length} 天
          </span>
        </div>
        {!isAdding && editingIndex === null && (
          <button
            onClick={handleAdd}
            className="bg-indigo-600 text-white px-3 py-2 rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-1 text-sm font-medium"
          >
            <Plus size={16} />
            新增行程
          </button>
        )}
      </div>

      {/* Schedule List */}
      <div className="space-y-2">
        {schedule.map((item, index) => {
          const isEditing = editingIndex === index;

          return (
            <div
              key={index}
              className={`border rounded-lg p-4 ${
                isEditing ? 'bg-indigo-50 border-indigo-200' : 'bg-gray-50 border-gray-200'
              }`}
            >
              {isEditing && editForm ? (
                // Edit Mode
                <div className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {/* Date */}
                    <div>
                      <label className="text-xs font-bold text-indigo-600 mb-1 block">
                        日期 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="如：06/20（五）"
                        className="w-full border border-indigo-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-200 outline-none"
                        value={editForm.date}
                        onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                      />
                    </div>

                    {/* Time of Day */}
                    <div>
                      <label className="text-xs font-bold text-indigo-600 mb-1 block">
                        時段 <span className="text-red-500">*</span>
                      </label>
                      <select
                        className="w-full border border-indigo-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-200 outline-none"
                        value={editForm.timeOfDay}
                        onChange={(e) => setEditForm({ ...editForm, timeOfDay: e.target.value as TimeOfDay })}
                      >
                        <option value={TimeOfDay.Morning}>上午</option>
                        <option value={TimeOfDay.Afternoon}>下午</option>
                        <option value={TimeOfDay.AllDay}>全天</option>
                        <option value={TimeOfDay.Overnight}>住宿</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {/* Location */}
                    <div>
                      <label className="text-xs font-bold text-indigo-600 mb-1 block">
                        地點名稱 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="如：金崙教會"
                        className="w-full border border-indigo-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-200 outline-none"
                        value={editForm.location}
                        onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                      />
                    </div>

                    {/* Activity */}
                    <div>
                      <label className="text-xs font-bold text-indigo-600 mb-1 block">
                        活動類型 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="如：義診、住宿、福音活動"
                        className="w-full border border-indigo-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-200 outline-none"
                        value={editForm.activity}
                        onChange={(e) => setEditForm({ ...editForm, activity: e.target.value })}
                      />
                    </div>
                  </div>

                  {/* Address */}
                  <div>
                    <label className="text-xs font-bold text-indigo-600 mb-1 block">
                      完整地址（選填）
                    </label>
                    <input
                      type="text"
                      placeholder="如：台東縣太麻里鄉金崙村3鄰68號"
                      className="w-full border border-indigo-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-200 outline-none"
                      value={editForm.address || ''}
                      onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                    />
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="text-xs font-bold text-indigo-600 mb-1 block">
                      備註（選填）
                    </label>
                    <input
                      type="text"
                      placeholder="如：白天進行義診服務"
                      className="w-full border border-indigo-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-200 outline-none"
                      value={editForm.notes || ''}
                      onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2 justify-end pt-2 border-t border-indigo-200">
                    <button
                      onClick={handleSave}
                      className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-1 text-sm"
                    >
                      <Save size={16} />
                      儲存
                    </button>
                    <button
                      onClick={handleCancel}
                      className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition-colors flex items-center gap-1 text-sm"
                    >
                      <X size={16} />
                      取消
                    </button>
                  </div>
                </div>
              ) : (
                // View Mode
                <div className="flex justify-between items-start">
                  <div className="flex-grow">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-bold text-gray-800">{item.date}</span>
                      <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">
                        {item.timeOfDay}
                      </span>
                    </div>
                    <div className="text-sm text-gray-700">
                      <span className="font-medium">{item.location}</span>
                      <span className="mx-2 text-gray-400">·</span>
                      <span>{item.activity}</span>
                    </div>
                    {item.address && (
                      <div className="text-xs text-gray-500 mt-1">
                        📍 {item.address}
                      </div>
                    )}
                    {item.notes && (
                      <div className="text-xs text-gray-500 italic mt-1">
                        💡 {item.notes}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => handleEdit(index)}
                      className="text-indigo-500 hover:text-indigo-700 p-2 rounded-lg hover:bg-indigo-50 transition-colors"
                      title="編輯"
                    >
                      <Save size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(index)}
                      className="text-red-500 hover:text-red-700 p-2 rounded-lg hover:bg-red-50 transition-colors"
                      title="刪除"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* Add New Form */}
        {isAdding && editForm && (
          <div className="border border-indigo-200 bg-indigo-50 rounded-lg p-4">
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-3 pb-3 border-b border-indigo-200">
                <Plus size={18} className="text-indigo-600" />
                <h4 className="font-bold text-indigo-800">新增行程項目</h4>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Date */}
                <div>
                  <label className="text-xs font-bold text-indigo-700 mb-1 block">
                    日期 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="如：06/20（五）"
                    className="w-full border border-indigo-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-200 outline-none"
                    value={editForm.date}
                    onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                  />
                </div>

                {/* Time of Day */}
                <div>
                  <label className="text-xs font-bold text-indigo-700 mb-1 block">
                    時段 <span className="text-red-500">*</span>
                  </label>
                  <select
                    className="w-full border border-indigo-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-200 outline-none"
                    value={editForm.timeOfDay}
                    onChange={(e) => setEditForm({ ...editForm, timeOfDay: e.target.value as TimeOfDay })}
                  >
                    <option value={TimeOfDay.Morning}>上午</option>
                    <option value={TimeOfDay.Afternoon}>下午</option>
                    <option value={TimeOfDay.AllDay}>全天</option>
                    <option value={TimeOfDay.Overnight}>住宿</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Location */}
                <div>
                  <label className="text-xs font-bold text-indigo-700 mb-1 block">
                    地點名稱 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="如：金崙教會"
                    className="w-full border border-indigo-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-200 outline-none"
                    value={editForm.location}
                    onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                  />
                </div>

                {/* Activity */}
                <div>
                  <label className="text-xs font-bold text-indigo-700 mb-1 block">
                    活動類型 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="如：義診、住宿、福音活動"
                    className="w-full border border-indigo-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-200 outline-none"
                    value={editForm.activity}
                    onChange={(e) => setEditForm({ ...editForm, activity: e.target.value })}
                  />
                </div>
              </div>

              {/* Address */}
              <div>
                <label className="text-xs font-bold text-indigo-700 mb-1 block">
                  完整地址（選填）
                </label>
                <input
                  type="text"
                  placeholder="如：台東縣太麻里鄉金崙村3鄰68號"
                  className="w-full border border-indigo-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-200 outline-none"
                  value={editForm.address || ''}
                  onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                />
              </div>

              {/* Notes */}
              <div>
                <label className="text-xs font-bold text-indigo-700 mb-1 block">
                  備註（選填）
                </label>
                <input
                  type="text"
                  placeholder="如：白天進行義診服務"
                  className="w-full border border-indigo-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-200 outline-none"
                  value={editForm.notes || ''}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 justify-end pt-2 border-t border-indigo-200">
                <button
                  onClick={handleSave}
                  className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-1 text-sm"
                >
                  <Save size={16} />
                  儲存
                </button>
                <button
                  onClick={handleCancel}
                  className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition-colors flex items-center gap-1 text-sm"
                >
                  <X size={16} />
                  取消
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Empty State */}
      {schedule.length === 0 && !isAdding && (
        <div className="text-center py-8 text-gray-400 border-2 border-dashed border-gray-200 rounded-lg">
          <Calendar size={48} className="mx-auto mb-2 opacity-30" />
          <p className="text-sm">尚未新增任何行程</p>
          <p className="text-xs mt-1">點擊「新增行程」開始建立詳細行程表</p>
        </div>
      )}
    </div>
  );
};

export default ScheduleEditor;
